import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";

import { useGetOrderByTCodeQuery } from "../store/api/orderAPI";
import {
    useGetOrderPvzQuery,
    useOrderPvzMutation,
} from "../store/api/orderSortApi";

import PresortDemo from "../components/table/DataTable";
import { orderColumnAcceptPVZ } from "../metadata/order";
import { useTranslatedColumns } from "../hooks/useTranslatedColumns";

import { Toast } from "primereact/toast";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";

export default function ScanReceive() {
    // Старый функционал ручного сканирования
    const [scanValue, setScanValue] = useState("");
    const [debouncedValue, setDebouncedValue] = useState("");
    const [scanList, setScanList] = useState([]);

    const { data } = useGetOrderPvzQuery();
    const [priemPVZ] = useOrderPvzMutation();

    const toast = useRef(null);
    const inputRef = useRef(null);
    const orderColumnTr = useTranslatedColumns(orderColumnAcceptPVZ);

    // Новый функционал массовой приёмки из файла
    const [receiveDialogVisible, setReceiveDialogVisible] = useState(false);
    const [file, setFile] = useState(null);
    const [trackingNumbers, setTrackingNumbers] = useState([]);
    const [results, setResults] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);

    const fileInputRef = useRef(null);
    const stopRef = useRef(false);
    const processingRef = useRef(false);

    // =========================================================
    // СТАРЫЙ ФУНКЦИОНАЛ: автоматическая приёмка ручного сканирования
    // =========================================================

    useEffect(() => {
        if (!scanValue.trim()) return;

        const handler = setTimeout(async () => {
            const trackingNumber = scanValue.trim();

            try {
                const res = await priemPVZ({
                    tracking_number: trackingNumber,
                });

                if (res.error || !res.data?.success) {
                    toast.current?.show({
                        severity: "warn",
                        summary: "Ошибка",
                        detail:
                            res.data?.message ||
                            res.error?.data?.message ||
                            "Ошибка при приёме заказа",
                        life: 2000,
                    });
                } else {
                    toast.current?.show({
                        severity: "success",
                        summary: "Принято",
                        detail:
                            res.data?.message ||
                            `Заказ ${trackingNumber} принят`,
                        life: 2000,
                    });
                }
            } catch (err) {
                toast.current?.show({
                    severity: "error",
                    summary: "Ошибка",
                    detail:
                        err.message ||
                        "Ошибка соединения с сервером",
                    life: 2000,
                });
            } finally {
                setScanValue("");
                setDebouncedValue("");
                inputRef.current?.focus();
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [scanValue]);

    // Старый запрос для получения информации о заказе
    const { data: order } = useGetOrderByTCodeQuery(
        { tcode: debouncedValue, status: 22 },
        { skip: !debouncedValue }
    );

    useEffect(() => {
        if (!order?.length) return;

        const scanned = order[0];

        if (
            scanList.some(
                (item) =>
                    item.tracking_number === scanned.tracking_number
            )
        ) {
            toast.current?.show({
                severity: "warn",
                summary: "Уже добавлено",
                detail: `Заказ ${scanned.tracking_number} уже в списке`,
                life: 2000,
            });
        } else {
            setScanList((prev) => [scanned, ...prev]);

            toast.current?.show({
                severity: "success",
                summary: "Добавлено",
                detail: `Заказ ${scanned.tracking_number} найден`,
                life: 2000,
            });
        }

        setScanValue("");
        setDebouncedValue("");
        inputRef.current?.focus();
    }, [order]);

    // =========================================================
    // НОВЫЙ ФУНКЦИОНАЛ: загрузка файла в popup
    // =========================================================

    const normalizeTracking = (value) =>
        String(value ?? "").trim();

    const parseExcel = (workbook) => {
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        if (!sheet) return [];

        const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            raw: false,
        });

        if (!rows.length) return [];

        const normalizeHeader = (value) =>
            String(value ?? "")
                .trim()
                .toLowerCase()
                .replace(/[\s_-]+/g, "");

        const possibleHeaders = [
            "trackingnumber",
            "tracking",
            "tcode",
            "треккод",
            "трекномер",
            "трек",
            "штрихкод",
            "trackingnumberпосылки",
        ];

        const firstRow = rows[0] || [];

        const headerIndex = firstRow.findIndex((cell) =>
            possibleHeaders.includes(normalizeHeader(cell))
        );

        // Если есть заголовок с трек-кодом, берём его столбец.
        // Иначе читаем первый столбец.
        const columnIndex = headerIndex >= 0 ? headerIndex : 0;
        const startIndex = headerIndex >= 0 ? 1 : 0;

        return rows
            .slice(startIndex)
            .map((row) => normalizeTracking(row[columnIndex]))
            .filter(Boolean);
    };

    const handleFileChange = async (event) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) return;

        const extension = selectedFile.name
            .split(".")
            .pop()
            .toLowerCase();

        try {
            let codes = [];

            if (extension === "txt") {
                const text = await selectedFile.text();

                codes = text
                    .split(/[\r\n,;\t]+/)
                    .map(normalizeTracking)
                    .filter(Boolean);
            } else if (
                ["xlsx", "xls", "csv"].includes(extension)
            ) {
                const buffer = await selectedFile.arrayBuffer();

                const workbook = XLSX.read(buffer, {
                    type: "array",
                    raw: false,
                });

                codes = parseExcel(workbook);
            } else {
                throw new Error(
                    "Поддерживаются файлы XLSX, XLS, CSV и TXT"
                );
            }

            const uniqueCodes = [...new Set(codes)];

            if (!uniqueCodes.length) {
                throw new Error(
                    "В файле не найдены трек-коды"
                );
            }

            setFile(selectedFile);
            setTrackingNumbers(uniqueCodes);
            setResults([]);
            setProgress(0);
            stopRef.current = false;

            toast.current?.show({
                severity: "success",
                summary: "Файл загружен",
                detail: `Найдено трек-кодов: ${uniqueCodes.length}`,
                life: 3000,
            });
        } catch (error) {
            setFile(null);
            setTrackingNumbers([]);

            toast.current?.show({
                severity: "error",
                summary: "Ошибка файла",
                detail:
                    error.message ||
                    "Не удалось прочитать файл",
                life: 3000,
            });
        } finally {
            // Разрешаем выбрать тот же файл повторно
            event.target.value = "";
        }
    };

    // =========================================================
    // НОВЫЙ ФУНКЦИОНАЛ: последовательная приёмка из файла
    // =========================================================

    const startReceiving = async () => {
        if (
            processingRef.current ||
            !trackingNumbers.length
        ) {
            return;
        }

        processingRef.current = true;
        stopRef.current = false;

        setIsProcessing(true);
        setProgress(0);
        setResults([]);

        const processedResults = [];
        const total = trackingNumbers.length;

        for (let i = 0; i < total; i++) {
            if (stopRef.current) break;

            const trackingNumber = trackingNumbers[i];

            try {
                const response = await priemPVZ({
                    tracking_number: trackingNumber,
                }).unwrap();

                if (response?.success) {
                    processedResults.push({
                        id: `${i}-${trackingNumber}`,
                        tracking_number: trackingNumber,
                        status: "success",
                        message:
                            response.message ||
                            "Посылка принята",
                    });
                } else {
                    processedResults.push({
                        id: `${i}-${trackingNumber}`,
                        tracking_number: trackingNumber,
                        status: "error",
                        message:
                            response?.message ||
                            "Приёмка не подтверждена сервером",
                    });
                }
            } catch (error) {
                const message =
                    error?.data?.message ||
                    error?.error ||
                    error?.message ||
                    "Ошибка соединения с сервером";

                processedResults.push({
                    id: `${i}-${trackingNumber}`,
                    tracking_number: trackingNumber,
                    status: "error",
                    message: Array.isArray(message)
                        ? message.join(", ")
                        : String(message),
                });
            }

            setResults([...processedResults]);
            setProgress(
                Math.round(((i + 1) / total) * 100)
            );
        }

        processingRef.current = false;
        setIsProcessing(false);

        try {
            await refetchOrders();
        } catch (error) {
            console.error(
                "Не удалось обновить список посылок:",
                error
            );
        }

        const successCount = processedResults.filter(
            (item) => item.status === "success"
        ).length;

        const errorCount = processedResults.filter(
            (item) => item.status === "error"
        ).length;

        toast.current?.show({
            severity: errorCount ? "warn" : "success",
            summary: stopRef.current
                ? "Приёмка остановлена"
                : "Приёмка завершена",
            detail: `Успешно: ${successCount}. Ошибок: ${errorCount}.`,
            life: 5000,
        });
    };

    // Получаем refetch для обновления серверной таблицы
    const { refetch: refetchOrders } = useGetOrderPvzQuery();

    const stopReceiving = () => {
        stopRef.current = true;
    };

    const clearFileData = () => {
        if (processingRef.current) return;

        setFile(null);
        setTrackingNumbers([]);
        setResults([]);
        setProgress(0);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const statusBody = (row) => (
        <Tag
            severity={
                row.status === "success" ? "success" : "danger"
            }
            value={
                row.status === "success" ? "Принято" : "Ошибка"
            }
        />
    );

    const successCount = results.filter(
        (item) => item.status === "success"
    ).length;

    const errorCount = results.filter(
        (item) => item.status === "error"
    ).length;

    // =========================================================
    // РЕНДЕР
    // =========================================================

    return (
        <div className="p-4">
            <Toast ref={toast} />

            {/* Старая панель ручного сканирования */}
            <div
                style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: 20,
                }}
                className="mb-3"
            >
                <h2 style={{ margin: 0 }}>
                    Приём посылок
                </h2>

                <IconField iconPosition="left">
                    <InputIcon className="pi pi-qrcode" />
                    <InputText
                        ref={inputRef}
                        value={scanValue}
                        onChange={(e) =>
                            setScanValue(e.target.value)
                        }
                        placeholder="Сканируйте штрих-код..."
                        style={{
                            paddingLeft: 40,
                            height: 45,
                        }}
                        autoFocus
                    />
                </IconField>

                {/* Новая кнопка открытия popup */}
                <Button
                    label="Приёмка из файла"
                    icon="pi pi-file-import"
                    style={{ height: 45 }}
                    onClick={() =>
                        setReceiveDialogVisible(true)
                    }
                />
            </div>

            {/* Старая таблица — оставлена на месте */}
            <PresortDemo
                data={data?.result ?? []}
                paginator
                column={orderColumnTr}
                toggleSidebar={() => {}}
            />

            {/* Popup автоматической приёмки из файла */}
            <Dialog
                header="Автоматическая приёмка из файла"
                visible={receiveDialogVisible}
                modal
                closable={!isProcessing}
                closeOnEscape={!isProcessing}
                style={{ width: "850px", maxWidth: "95vw" }}
                onHide={() => {
                    if (!isProcessing) {
                        setReceiveDialogVisible(false);
                    }
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                    }}
                >
                    <div>
                        Загрузите файл с трек-кодами. Поддерживаются
                        Excel, CSV и TXT. После загрузки нажмите
                        «Запустить приёмку».
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv,.txt"
                        onChange={handleFileChange}
                        disabled={isProcessing}
                    />

                    {file && (
                        <div>
                            <div>
                                <strong>Файл:</strong>{" "}
                                {file.name}
                            </div>
                            <div>
                                <strong>Уникальных трек-кодов:</strong>{" "}
                                {trackingNumbers.length}
                            </div>
                        </div>
                    )}

                    {isProcessing && (
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                Обработка: {progress}%
                            </div>
                            <ProgressBar value={progress} />
                        </div>
                    )}

                    {results.length > 0 && (
                        <div
                            style={{
                                display: "flex",
                                gap: 20,
                                flexWrap: "wrap",
                            }}
                        >
                            <span>
                                Обработано: <strong>{results.length}</strong>
                            </span>
                            <span>
                                Принято: <strong>{successCount}</strong>
                            </span>
                            <span>
                                Ошибок: <strong>{errorCount}</strong>
                            </span>
                        </div>
                    )}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            label="Очистить"
                            icon="pi pi-trash"
                            severity="secondary"
                            outlined
                            disabled={isProcessing}
                            onClick={clearFileData}
                        />

                        {isProcessing ? (
                            <Button
                                label="Остановить"
                                icon="pi pi-stop"
                                severity="danger"
                                onClick={stopReceiving}
                            />
                        ) : (
                            <Button
                                label="Запустить приёмку"
                                icon="pi pi-play"
                                disabled={
                                    trackingNumbers.length === 0
                                }
                                onClick={startReceiving}
                            />
                        )}
                    </div>

                    <DataTable
                        value={results}
                        dataKey="id"
                        paginator
                        rows={10}
                        stripedRows
                        showGridlines
                        emptyMessage="Результаты появятся после запуска приёмки"
                        responsiveLayout="scroll"
                    >
                        <Column
                            field="tracking_number"
                            header="Трек-код"
                        />
                        <Column
                            header="Статус"
                            body={statusBody}
                            style={{ width: 120 }}
                        />
                        <Column
                            field="message"
                            header="Результат"
                        />
                    </DataTable>
                </div>
            </Dialog>
        </div>
    );
}